/**
 * useTypingIndicator Hook
 *
 * Debounced typing indicator that writes to conversation.typing map.
 * Writes on keystroke start, clears after 5s idle.
 */

import { useRef, useCallback, useEffect } from 'react';
import ConversationService from '@/services/messaging/ConversationService';
import { useAuth } from '@/hooks';

const TYPING_TIMEOUT = 5000; // Clear after 5s idle

export default function useTypingIndicator(conversationId) {
  const { user } = useAuth();
  const timeoutRef = useRef(null);
  const isTypingRef = useRef(false);

  // Clear typing on unmount or conversation change
  useEffect(() => {
    return () => {
      if (isTypingRef.current && conversationId && user?.uid) {
        ConversationService.setTyping(conversationId, user.uid, false);
        isTypingRef.current = false;
      }
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [conversationId, user?.uid]);

  /**
   * Call this on each keystroke in the input
   */
  const onKeyStroke = useCallback(() => {
    if (!conversationId || !user?.uid) return;

    // Start typing if not already
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      ConversationService.setTyping(conversationId, user.uid, true);
    }

    // Reset timeout
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (isTypingRef.current) {
        isTypingRef.current = false;
        ConversationService.setTyping(conversationId, user.uid, false);
      }
    }, TYPING_TIMEOUT);
  }, [conversationId, user?.uid]);

  /**
   * Call when message is sent (clear immediately)
   */
  const clearTyping = useCallback(() => {
    if (!conversationId || !user?.uid) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (isTypingRef.current) {
      isTypingRef.current = false;
      ConversationService.setTyping(conversationId, user.uid, false);
    }
  }, [conversationId, user?.uid]);

  return { onKeyStroke, clearTyping };
}
